/**
 * Texto: medir, quebrar, encolher, formatar data.
 *
 * Tudo aqui e deterministico por construcao. Duas proibicoes valem para o
 * arquivo inteiro:
 *
 * 1. **Nada de `Intl`.** O ICU do Deno Deploy nao e o do Node, e nao e o do
 *    navegador — e muda entre releases sem aviso. `Intl.DateTimeFormat` com o
 *    mesmo instante pode devolver "27 de agosto" num runtime e "27 de ago."
 *    noutro. A formatacao de data vive numa tabela deste arquivo.
 *
 * 2. **Nada de `Math.sin/cos/pow/exp/log`.** A especificacao ECMA permite
 *    resultado dependente de implementacao nessas funcoes. Aritmetica aqui e
 *    soma, subtracao, multiplicacao e divisao.
 *
 * A medida acumula em UNIDADES DE FONTE (inteiros) e divide por `unitsPerEm`
 * uma unica vez, no fim. Somar 500 inteiros da o mesmo resultado em qualquer
 * runtime; somar 500 floats nao da.
 */

import { advanceWidth, unitsPerEm, type FontId } from '../../fonts/metrics.js';

// ─── Medida ─────────────────────────────────────────────────────────────────

/** Code point substituto quando a fonte nao tem o glifo. */
const FALLBACK_CP = 0x003f; // '?'

export interface MedidaResultado {
  /** Largura em pontos. */
  readonly larguraPt: number;
  /** Code points sem glifo na fonte. */
  readonly semGlifo: readonly number[];
}

/**
 * Largura de uma linha, em pontos.
 *
 * `letterSpacing` entra (n-1) vezes, nao n: o espacamento fica ENTRE
 * caracteres. Aplicar depois do ultimo deslocaria o centro do bloco na
 * centralizacao, e o texto sairia levemente a esquerda do que o produtor
 * posicionou.
 */
export function medirLinha(
  texto: string,
  font: FontId,
  tamanhoPt: number,
  letterSpacingEm = 0,
): MedidaResultado {
  const pontos = [...texto];
  if (pontos.length === 0) return { larguraPt: 0, semGlifo: [] };

  let unidades = 0;
  const semGlifo: number[] = [];

  for (const ch of pontos) {
    const cp = ch.codePointAt(0)!;
    const a = advanceWidth(cp, font);
    if (a === undefined) {
      semGlifo.push(cp);
      unidades += advanceWidth(FALLBACK_CP, font) ?? 0;
    } else {
      unidades += a;
    }
  }

  const larguraGlifos = (unidades * tamanhoPt) / unitsPerEm(font);
  const extra = letterSpacingEm * tamanhoPt * (pontos.length - 1);
  return { larguraPt: larguraGlifos + extra, semGlifo };
}

/** So a largura. Atalho para quando os glifos ausentes nao interessam. */
export function larguraDe(
  texto: string,
  font: FontId,
  tamanhoPt: number,
  letterSpacingEm = 0,
): number {
  return medirLinha(texto, font, tamanhoPt, letterSpacingEm).larguraPt;
}

// ─── Sanitizacao ────────────────────────────────────────────────────────────

/**
 * Substitui por '?' o que a fonte nao consegue desenhar, depois de tentar
 * decompor o acento.
 *
 * "ǅ" nao existe no subset, mas decompoe em "D" + "ž". Tentar NFD antes de
 * desistir salva boa parte dos casos sem inventar glifo.
 */
export function sanitizarParaFonte(texto: string, font: FontId): {
  texto: string;
  substituidos: readonly number[];
} {
  const substituidos: number[] = [];
  let saida = '';

  for (const ch of texto) {
    const cp = ch.codePointAt(0)!;
    if (advanceWidth(cp, font) !== undefined) {
      saida += ch;
      continue;
    }

    // Tenta decompor: "ǎ" -> "a" + combinante. Fica com a base se ela existir.
    const decomposto = ch.normalize('NFD');
    const base = [...decomposto].filter((c) => advanceWidth(c.codePointAt(0)!, font) !== undefined);
    if (base.length > 0) {
      saida += base.join('');
    } else {
      saida += '?';
      substituidos.push(cp);
    }
  }

  return { texto: saida, substituidos };
}

// ─── Quebra de linha ────────────────────────────────────────────────────────

/**
 * Quebra gulosa por palavras, com fallback por grafema.
 *
 * Uma palavra maior que a caixa (URL longa, nome sem espaco) e quebrada
 * caractere a caractere em vez de vazar. Vazar e pior: o texto invade os
 * elementos vizinhos e o produtor nao ve isso no editor.
 */
export function quebrarEmLinhas(
  texto: string,
  font: FontId,
  tamanhoPt: number,
  larguraMaxPt: number,
  letterSpacingEm = 0,
): string[] {
  if (!texto) return [];
  if (larguraMaxPt <= 0) return [texto];

  const palavras = texto.split(' ').filter((p) => p.length > 0);
  if (palavras.length === 0) return [];

  const linhas: string[] = [];
  let atual = '';

  const cabe = (s: string) => larguraDe(s, font, tamanhoPt, letterSpacingEm) <= larguraMaxPt;

  for (const palavra of palavras) {
    const tentativa = atual ? `${atual} ${palavra}` : palavra;

    if (cabe(tentativa)) {
      atual = tentativa;
      continue;
    }

    if (atual) {
      linhas.push(atual);
      atual = '';
    }

    if (cabe(palavra)) {
      atual = palavra;
      continue;
    }

    // Palavra sozinha nao cabe: quebra por grafema.
    let pedaco = '';
    for (const g of palavra) {
      if (pedaco && !cabe(pedaco + g)) {
        linhas.push(pedaco);
        pedaco = g;
      } else {
        pedaco += g;
      }
    }
    atual = pedaco;
  }

  if (atual) linhas.push(atual);
  return linhas;
}

// ─── Ajuste ao espaco disponivel ────────────────────────────────────────────

export interface AjusteResultado {
  readonly linhas: readonly string[];
  /** Corpo efetivo, em pontos. Pode ser menor que o pedido. */
  readonly tamanhoPt: number;
  readonly encolheu: boolean;
  readonly truncou: boolean;
}

const PASSO_ENCOLHER_PT = 0.5;

/**
 * Encaixa o texto na caixa: reduz o corpo de 0,5pt por vez ate caber em
 * `maxLinhas`; se ainda nao couber no menor corpo, trunca com reticencia.
 *
 * Nunca vaza. Um nome muito longo sai menor, ou cortado — mas dentro da caixa
 * que o produtor desenhou.
 */
export function ajustarAoEspaco(
  texto: string,
  font: FontId,
  tamanhoPt: number,
  larguraMaxPt: number,
  maxLinhas: number,
  letterSpacingEm = 0,
  minTamanhoPt = 6,
): AjusteResultado {
  if (!texto) {
    return { linhas: [], tamanhoPt, encolheu: false, truncou: false };
  }

  let tamanho = tamanhoPt;
  let linhas = quebrarEmLinhas(texto, font, tamanho, larguraMaxPt, letterSpacingEm);

  while (linhas.length > maxLinhas && tamanho - PASSO_ENCOLHER_PT >= minTamanhoPt) {
    tamanho -= PASSO_ENCOLHER_PT;
    linhas = quebrarEmLinhas(texto, font, tamanho, larguraMaxPt, letterSpacingEm);
  }

  const encolheu = tamanho !== tamanhoPt;

  if (linhas.length <= maxLinhas) {
    return { linhas, tamanhoPt: tamanho, encolheu, truncou: false };
  }

  // Nao coube nem no menor corpo: mantem as primeiras linhas e poe reticencia.
  const mantidas = linhas.slice(0, maxLinhas);
  const ultima = mantidas[maxLinhas - 1] ?? '';
  let cortada = ultima;
  while (
    cortada.length > 0 &&
    larguraDe(`${cortada}…`, font, tamanho, letterSpacingEm) > larguraMaxPt
  ) {
    cortada = [...cortada].slice(0, -1).join('');
  }
  mantidas[maxLinhas - 1] = `${cortada}…`;

  return { linhas: mantidas, tamanhoPt: tamanho, encolheu, truncou: true };
}

// ─── Data e hora, sem Intl ──────────────────────────────────────────────────

const MESES_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
] as const;

/**
 * Deslocamento em minutos por fuso, sem consultar o sistema.
 *
 * O Brasil nao tem horario de verao desde 2019, entao um deslocamento fixo por
 * fuso e correto para todo evento que este sistema emite. Fuso desconhecido
 * cai em UTC e emite diagnostico — melhor uma data explicitamente em UTC do
 * que uma data errada com cara de certa.
 */
const OFFSET_MIN: Readonly<Record<string, number>> = {
  'America/Sao_Paulo': -180,
  'America/Bahia': -180,
  'America/Fortaleza': -180,
  'America/Recife': -180,
  'America/Belem': -180,
  'America/Araguaina': -180,
  'America/Maceio': -180,
  'America/Campo_Grande': -240,
  'America/Cuiaba': -240,
  'America/Manaus': -240,
  'America/Porto_Velho': -240,
  'America/Boa_Vista': -240,
  'America/Rio_Branco': -300,
  'America/Eirunepe': -300,
  'America/Noronha': -120,
  UTC: 0,
};

export function offsetConhecido(timeZone: string): boolean {
  return timeZone in OFFSET_MIN;
}

export interface PartesData {
  readonly ano: number;
  readonly mes: number; // 1-12
  readonly dia: number;
  readonly hora: number;
  readonly minuto: number;
}

/** ISO -> componentes locais do fuso, por aritmetica de milissegundos. */
export function partesNoFuso(iso: string, timeZone: string): PartesData | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;

  const deslocado = new Date(ms + (OFFSET_MIN[timeZone] ?? 0) * 60_000);
  return {
    ano: deslocado.getUTCFullYear(),
    mes: deslocado.getUTCMonth() + 1,
    dia: deslocado.getUTCDate(),
    hora: deslocado.getUTCHours(),
    minuto: deslocado.getUTCMinutes(),
  };
}

const dois = (n: number) => (n < 10 ? `0${n}` : String(n));

/** "27 de agosto de 2026" */
export function dataPorExtenso(p: PartesData): string {
  return `${p.dia} de ${MESES_PT[p.mes - 1]} de ${p.ano}`;
}

/** "27/08/2026" */
export function dataCurta(p: PartesData): string {
  return `${dois(p.dia)}/${dois(p.mes)}/${p.ano}`;
}

/** "14:30" */
export function hora(p: PartesData): string {
  return `${dois(p.hora)}:${dois(p.minuto)}`;
}

export type ModoData = 'start' | 'end' | 'range';

/**
 * A data do evento, em formato unico.
 *
 * Hoje existem TRES formatos diferentes para o mesmo certificado, porque cada
 * caminho de render passa a sua propria funcao de formatacao. Aqui e um so.
 *
 * Intervalo dentro do mesmo mes encurta: "20 a 22 de agosto de 2026". Cruzando
 * mes ou ano, escreve as duas datas por extenso.
 */
export function formatarDataEvento(
  inicioIso: string | null,
  fimIso: string | null,
  modo: ModoData,
  timeZone: string,
): string {
  const ini = inicioIso ? partesNoFuso(inicioIso, timeZone) : null;
  const fim = fimIso ? partesNoFuso(fimIso, timeZone) : null;

  if (modo === 'end') {
    const alvo = fim ?? ini;
    return alvo ? dataPorExtenso(alvo) : '';
  }

  if (modo === 'range' && ini && fim) {
    const mesmoDia = ini.ano === fim.ano && ini.mes === fim.mes && ini.dia === fim.dia;
    if (mesmoDia) return dataPorExtenso(ini);

    if (ini.ano === fim.ano && ini.mes === fim.mes) {
      return `${ini.dia} a ${fim.dia} de ${MESES_PT[ini.mes - 1]} de ${ini.ano}`;
    }
    return `${dataPorExtenso(ini)} a ${dataPorExtenso(fim)}`;
  }

  return ini ? dataPorExtenso(ini) : fim ? dataPorExtenso(fim) : '';
}

/**
 * Carga horaria: 8 -> "8h", 2.5 -> "2h30".
 *
 * Fracionario vira minutos em vez de decimal: "2,5h" le mal num certificado.
 */
export function formatarCargaHoraria(horas: number): string {
  const inteiras = Math.floor(horas);
  const minutos = Math.round((horas - inteiras) * 60);
  if (minutos === 0) return `${inteiras}h`;
  if (minutos === 60) return `${inteiras + 1}h`;
  return `${inteiras}h${dois(minutos)}`;
}

/** Aplica caixa alta quando o estilo pede. */
export function aplicarCaixa(texto: string, uppercase: boolean): string {
  return uppercase ? texto.toUpperCase() : texto;
}
